import { GitPullRequest, Loader2 } from 'lucide-react';
import type { QmsAiFindings } from '../../../types/qms.types';
import { Button } from '@/components/ui/button';

export function QmsCodeFixStatus({ fix, onRetry, isRetrying }: {
  fix?: QmsAiFindings['codeFix']; onRetry?: () => void; isRetrying?: boolean;
}) {
  if (!fix || fix.status === 'not_applicable') return null;
  const validUrl = fix.pullRequestUrl && /^https:\/\/github\.com\/ilyasatek\/monorepo\/pull\/[1-9]\d*$/.test(fix.pullRequestUrl);
  if (fix.status === 'pr_created' && validUrl) {
    return (
      <div className="mt-3 rounded-md border border-purple-200 bg-purple-50 px-4 py-3 text-sm">
        <a href={fix.pullRequestUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-medium text-purple-800 underline underline-offset-2">
          <GitPullRequest className="h-4 w-4" />
          View proposed code fix{fix.pullRequestNumber ? ` #${fix.pullRequestNumber}` : ''}
        </a>
        <p className="mt-1 text-xs text-gray-600">Review and CI checks are required before merging.</p>
      </div>
    );
  }
  const pending = fix.status === 'pending' || fix.status === 'in_progress';
  const legacyRejection = fix.status === 'needs_review' && !fix.reasonCode
    && fix.reason === 'The proposed patch did not meet source, scope or regression-test requirements.';
  const nextStep = fix.nextStep ?? (legacyRejection
    ? 'Retry code fix to make a new attempt and record a specific reason if validation rejects it again.' : undefined);
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700" role="status">
      <span className="inline-flex items-center gap-2 font-medium">
        {pending && <Loader2 className="h-4 w-4 animate-spin" />}
        {pending ? 'Preparing a proposed code fix' : fix.status === 'failed' ? 'Code fix could not be completed'
          : fix.status === 'disabled' ? 'Automatic code fixes are disabled'
          : fix.sourceReview ? 'No new code fix proposed' : 'Code fix needs review'}
      </span>
      {!pending && fix.reason && <p className="mt-1">{legacyRejection
        ? 'The proposed code fix was rejected, but this earlier attempt did not record which validation check failed.'
        : fix.reason}</p>}
      {!pending && nextStep && <p className="mt-1">{nextStep}</p>}
      {!pending && fix.sourceReview && (
        <div className="mt-3 border-t border-gray-200 pt-2">
          <p className="font-medium">AI source review</p>
          <p className="mt-1 whitespace-pre-wrap break-words">{fix.sourceReview.explanation}</p>
          <ul className="mt-2 list-inside list-disc break-all text-xs text-gray-600">
            {fix.sourceReview.evidence.map((item, index) => <li key={`${item.path}:${item.line}:${index}`}>{item.path}:{item.line}</li>)}
          </ul>
          {fix.baseSha && <p className="mt-1 text-xs text-gray-600">Inspected revision: {fix.baseSha.slice(0, 12)}</p>}
        </div>
      )}
      {!pending && onRetry && ['failed', 'needs_review', 'disabled'].includes(fix.status) && (
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? 'Retrying…' : 'Retry code fix'}
        </Button>
      )}
    </div>
  );
}
